
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSupabaseAuth } from "@/hooks/useSupabaseAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import NavigationSection from "./mobile/NavigationSection";
import AccountSection from "./mobile/AccountSection";
import { useLanguage } from "@/contexts/LanguageContext";

interface MobileMenuProps {
  user: any;
  isOpen: boolean;
  onClose: () => void;
  onAuthModalOpen: (tab: "signin" | "signup") => void;
}

export default function MobileMenu({ 
  user, 
  isOpen, 
  onClose,
  onAuthModalOpen 
}: MobileMenuProps) {
  const { isAdmin, isLoading } = useSupabaseAuth();
  const navigate = useNavigate();
  const showAdminLink = isAdmin;
  const { t } = useLanguage();
  
  useEffect(() => {
    // Log admin status changes for debugging
    if (user) {
      console.log("MobileMenu - User authenticated, admin status:", { 
        userId: user.id,
        email: user.email,
        isAdmin, 
        isLoading,
        showAdminLink
      });
    }
  }, [user, isAdmin, isLoading, showAdminLink]);
  
  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      onClose();
      toast.success(t('auth.signedOutSuccess'));
      
      // Redirect to main page if on auth-protected routes
      const authProtectedRoutes = ['/profile', '/admin', '/favorites'];
      const currentPath = window.location.pathname;
      if (authProtectedRoutes.some(route => currentPath.startsWith(route))) {
        navigate('/');
      }
    } catch (error) {
      console.error("Error signing out:", error);
      toast.error(t('auth.signOutError'));
    }
  };

  // Standard admin click handler
  const handleAdminClick = () => {
    onClose();
    navigate("/admin");
    return true;
  };

  if (!isOpen) return null;

  return (
    <div 
      className="bg-background border-t pt-2 pb-4 animate-fade-in" 
      data-mobile-menu="true" 
      data-open={isOpen.toString()}
    >
      <div className="container mx-auto px-4">
        <div className="flex flex-col h-full">
          <div className="py-4 flex-1">
            <NavigationSection onItemClick={onClose} />

            <AccountSection
              user={user}
              isAdmin={isAdmin}
              isLoading={isLoading}
              showAdminLink={showAdminLink}
              onClose={onClose}
              onAuthModalOpen={onAuthModalOpen}
              handleSignOut={handleSignOut}
              handleAdminClick={handleAdminClick}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
